import type { Question } from '../../types';

// ---------------------------------------------------------------------------
// Artisan / Specialty Contractor Supplement
// All questions conditioned on industry_type eq 'artisan_contractor'
// (or any subcategory that maps to the artisan_contractor supplement)
// ---------------------------------------------------------------------------

const ARTISAN_CONDITION = [
  { field: 'industry_type', operator: 'eq' as const, value: 'artisan_contractor' },
];

export const artisanContractorQuestions: Question[] = [
  {
    id: 'ac_years_experience',
    text: 'How many years of experience does the principal have in this trade?',
    section: 'supplement',
    group: 'artisan_experience',
    sort_order: 10,
    field_type: 'number',
    placeholder: '10',
    required: true,
    validation: { min: 0, max: 75 },
    field_path: 'supplement.artisan.years_experience',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_license_number',
    text: 'Contractor license number',
    subtext: 'If your state requires a contractor license.',
    section: 'supplement',
    group: 'artisan_experience',
    sort_order: 20,
    field_type: 'text',
    placeholder: 'ABC-123456',
    required: false,
    field_path: 'supplement.artisan.license_number',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_license_state',
    text: 'State where license is held',
    section: 'supplement',
    group: 'artisan_experience',
    sort_order: 30,
    field_type: 'state_select',
    required: false,
    field_path: 'supplement.artisan.license_state',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_license_shared',
    text: 'Is your license shared with or rented to any other business?',
    section: 'supplement',
    group: 'artisan_experience',
    sort_order: 40,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.artisan.license_shared',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_pct_artisan',
    text: 'What percentage of work is performed by your own employees (artisan)?',
    subtext: 'The remainder is assumed to be subcontracted.',
    section: 'supplement',
    group: 'artisan_operations',
    sort_order: 50,
    field_type: 'percentage',
    placeholder: '80',
    required: true,
    validation: { min: 0, max: 100 },
    field_path: 'supplement.artisan.pct_artisan',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_gross_sales_prior',
    text: 'Prior year gross sales / receipts',
    section: 'supplement',
    group: 'artisan_financials',
    sort_order: 60,
    field_type: 'currency',
    prefix: '$',
    placeholder: '500000',
    required: true,
    field_path: 'supplement.artisan.gross_sales_prior',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_gross_sales_anticipated',
    text: 'Anticipated gross sales / receipts for the coming year',
    section: 'supplement',
    group: 'artisan_financials',
    sort_order: 70,
    field_type: 'currency',
    prefix: '$',
    placeholder: '600000',
    required: true,
    field_path: 'supplement.artisan.gross_sales_anticipated',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_owner_count',
    text: 'Number of owners / officers / partners',
    section: 'supplement',
    group: 'artisan_owners',
    sort_order: 80,
    field_type: 'number',
    placeholder: '1',
    required: true,
    validation: { min: 1, max: 20 },
    field_path: 'supplement.artisan.owner_count',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_owner_trades',
    text: 'What trade(s) do the owners perform?',
    subtext: 'List all trades owners actively work in.',
    section: 'supplement',
    group: 'artisan_owners',
    sort_order: 90,
    field_type: 'textarea',
    placeholder: 'Electrical wiring, panel installations',
    required: true,
    field_path: 'supplement.artisan.owner_trades',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_employee_matrix',
    text: 'Employee breakdown by trade',
    subtext: 'For each trade, provide the number of employees and estimated annual payroll.',
    section: 'supplement',
    group: 'artisan_employees',
    sort_order: 100,
    field_type: 'trade_matrix',
    required: true,
    field_path: 'supplement.artisan.employee_matrix',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_uses_subcontractors',
    text: 'Do you use subcontractors?',
    section: 'supplement',
    group: 'artisan_subcontractors',
    sort_order: 110,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.artisan.uses_subcontractors',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_sub_annual_cost',
    text: 'Annual cost of subcontractors',
    section: 'supplement',
    group: 'artisan_subcontractors',
    sort_order: 120,
    field_type: 'currency',
    prefix: '$',
    placeholder: '100000',
    required: true,
    field_path: 'supplement.artisan.sub_annual_cost',
    conditions: [
      ...ARTISAN_CONDITION,
      { field: 'ac_uses_subcontractors', operator: 'eq' as const, value: true },
    ],
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_sub_insurance_required',
    text: 'Do you require subcontractors to carry their own insurance?',
    section: 'supplement',
    group: 'artisan_subcontractors',
    sort_order: 130,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.artisan.sub_insurance_required',
    conditions: [
      ...ARTISAN_CONDITION,
      { field: 'ac_uses_subcontractors', operator: 'eq' as const, value: true },
    ],
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_condo_townhouse_work',
    text: 'Do you perform work on condominiums, townhouses, or tract housing?',
    section: 'supplement',
    group: 'artisan_risk',
    sort_order: 140,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.artisan.condo_townhouse_work',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_operating_states',
    text: 'In which states do you operate?',
    subtext: 'Select all states where you perform work.',
    section: 'supplement',
    group: 'artisan_territory',
    sort_order: 150,
    field_type: 'multiselect',
    required: true,
    field_path: 'supplement.artisan.operating_states',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_ny_exposure',
    text: 'Do you perform any work in New York?',
    subtext:
      'New York has special labor law requirements (Scaffold Law) that affect coverage and pricing.',
    section: 'supplement',
    group: 'artisan_territory',
    sort_order: 160,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.artisan.ny_exposure',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan', 'state_specific'],
  },
  {
    id: 'ac_does_framing',
    text: 'Does your company perform any framing work?',
    section: 'supplement',
    group: 'artisan_risk',
    sort_order: 170,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.artisan.does_framing',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_does_foundation',
    text: 'Does your company perform any foundation or structural work?',
    section: 'supplement',
    group: 'artisan_risk',
    sort_order: 180,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.artisan.does_foundation',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_does_roofing',
    text: 'Does your company perform any roofing work?',
    section: 'supplement',
    group: 'artisan_risk',
    sort_order: 190,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.artisan.does_roofing',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_does_eifs',
    text: 'Does your company work with EIFS (Exterior Insulation Finishing Systems)?',
    section: 'supplement',
    group: 'artisan_risk',
    sort_order: 200,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.artisan.does_eifs',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
  {
    id: 'ac_hazmat_work',
    text: 'Does your company handle or remove hazardous materials (asbestos, lead, mold)?',
    section: 'supplement',
    group: 'artisan_risk',
    sort_order: 210,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.artisan.hazmat_work',
    conditions: ARTISAN_CONDITION,
    tags: ['supplement', 'artisan'],
  },
];
